import React, { useMemo, useRef } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import ContactForm from '../components/ContactForm';
import {
  FaPaintBrush,
  FaRobot,
  FaIdBadge,
  FaBullhorn,
  FaChalkboard,
  FaShareAlt,
  FaChartPie,
  FaBoxOpen,
  FaLaptopCode,
  FaPenNib,
} from 'react-icons/fa';
import bgImage from '../assets/mobapp.jpg';

/**
 * Graphics & AI-Driven Design page styled like CyberSecurityPage
 * - semantic structure, reduced-motion support, responsive typography
 */

const gradientText = {
  background: 'linear-gradient(to right, #ec4899, #8b5cf6)', // pink → violet
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
};

const wrapStyle = (imageUrl) => ({
  minHeight: '100vh',
  padding: '80px 20px',
  color: '#fff',
  fontFamily: "'Inter', 'Segoe UI', sans-serif",
  backgroundAttachment: 'fixed',
  backgroundImage: `linear-gradient(rgba(15,23,42,0.92), rgba(2,6,23,0.95)), url(${imageUrl})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
});

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
  gap: '30px',
  maxWidth: '1200px',
  margin: '0 auto',
};

const cardStyleBase = {
  borderRadius: '16px',
  padding: '30px',
  color: '#fff',
  textAlign: 'left',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  backdropFilter: 'blur(12px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.3)',
};

const iconWrapStyle = {
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '1.5rem',
  marginBottom: '20px',
  background: 'rgba(255, 255, 255, 0.15)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
};

const sectionShell = {
  background: 'linear-gradient(to right, rgba(30,41,59,0.8), rgba(15,23,42,0.9))',
  borderRadius: '24px',
  padding: '50px',
  backdropFilter: 'blur(12px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  boxShadow: '0 20px 25px -5px rgba(0,0,0,0.3)',
  maxWidth: 'min(1000px, 90vw)',
  width: '100%',
  margin: '80px auto 0',
};

const listGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))',
  gap: '20px',
  maxWidth: '900px',
  margin: '0 auto',
  padding: 0,
  listStyle: 'none',
};

const GraphicsDesign = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });
  const prefersReducedMotion = useReducedMotion();

  const services = useMemo(
    () => [
      {
        icon: <FaPaintBrush aria-hidden="true" />,
        title: 'Graphic Design',
        description:
          'Logos, print, and digital assets that express your brand values with clarity. Creativity meets function for visuals that resonate and stick.',
        color: 'linear-gradient(135deg,#8b5cf6,#d946ef)',
      },
      {
        icon: <FaRobot aria-hidden="true" />,
        title: 'AI-Powered Design Generation',
        description:
          'AI‑assisted creation for unique, tailored visuals at speed—enabling personalization and rapid iteration without sacrificing originality.',
        color: 'linear-gradient(135deg,#06b6d4,#0ea5e9)',
      },
      {
        icon: <FaIdBadge aria-hidden="true" />,
        title: 'Brand Identity Packages',
        description:
          'Comprehensive visual systems—color, type, components—for consistency across every platform and touchpoint.',
        color: 'linear-gradient(135deg,#f97316,#ef4444)',
      },
      {
        icon: <FaBullhorn aria-hidden="true" />,
        title: 'Marketing Collateral',
        description:
          'Brochures, flyers, banners, and digital assets tailored to your message—elevating credibility and campaign performance.',
        color: 'linear-gradient(135deg,#3b82f6,#0ea5e9)',
      },
      {
        icon: <FaChalkboard aria-hidden="true" />,
        title: 'Presentation Design',
        description:
          'Clear layouts and engaging visuals that support storytelling and make messages memorable.',
        color: 'linear-gradient(135deg,#10b981,#14b8a6)',
      },
      {
        icon: <FaShareAlt aria-hidden="true" />,
        title: 'Social Media Creative',
        description:
          'Attention‑grabbing visuals aligned to your brand voice to boost awareness, interaction, and consistency across channels.',
        color: 'linear-gradient(135deg,#111827,#6b7280)',
      },
      {
        icon: <FaChartPie aria-hidden="true" />,
        title: 'Infographics & Data Visuals',
        description:
          'Turn complexity into clarity. Data‑driven visuals that highlight key points and increase comprehension and engagement.',
        color: 'linear-gradient(135deg,#22c55e,#10b981)',
      },
      {
        icon: <FaBoxOpen aria-hidden="true" />,
        title: 'Print & Packaging Design',
        description:
          'Tangible touchpoints that create strong first impressions and elevate perceived value and professionalism.',
        color: 'linear-gradient(135deg,#f59e0b,#ef4444)',
      },
      {
        icon: <FaLaptopCode aria-hidden="true" />,
        title: 'Website & Landing Page Design (UI-only)',
        description:
          'Aesthetic, intuitive layouts that guide visitors toward key actions and reinforce your brand image.',
        color: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
      },
      {
        icon: <FaPenNib aria-hidden="true" />,
        title: 'Custom Illustrations & Icons',
        description:
          'Unique illustration systems and iconography that add personality and distinctiveness across digital and print.',
        color: 'linear-gradient(135deg,#ec4899,#f472b6)',
      },
    ],
    []
  );

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: prefersReducedMotion
        ? { duration: 0 }
        : { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { y: prefersReducedMotion ? 0 : 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: prefersReducedMotion
        ? { duration: 0 }
        : { type: 'spring', stiffness: 100, damping: 10 },
    },
  };

  return (
    <main style={wrapStyle(bgImage)} ref={ref} aria-label="Graphics & AI-Driven Design Page">
      <motion.div
        initial="hidden"
        animate={isInView ? 'visible' : 'hidden'}
        variants={containerVariants}
        style={{ maxWidth: '1280px', margin: '0 auto' }}
      >
        <header style={{ textAlign: 'center' }}>
          <motion.h1
            style={{
              ...gradientText,
              fontSize: 'clamp(2.25rem, 3vw + 1rem, 3.5rem)',
              fontWeight: 700,
              marginBottom: 24,
            }}
            variants={itemVariants}
          >
            Graphic & AI-Driven Design
          </motion.h1>

          <motion.p
            style={{
              fontSize: 'clamp(1rem, 0.5vw + 0.9rem, 1.25rem)',
              maxWidth: 900,
              margin: '0 auto 60px',
              color: '#d1d5db',
              lineHeight: 1.7,
            }}
            variants={itemVariants}
          >
            We deliver creative solutions that make your brand stand out across every platform. By blending traditional craft with AI‑driven innovation, we offer efficiency, originality, and consistency—so your brand connects through compelling visuals.
          </motion.p>
        </header>

        <section aria-labelledby="services-heading">
          <h2 id="services-heading" style={{ position: 'absolute', left: -10000 }}>
            Our Design Services
          </h2>

          <motion.div style={gridStyle} variants={containerVariants}>
            {services.map((s) => (
              <motion.article
                key={s.title}
                aria-label={s.title}
                style={{ ...cardStyleBase, background: s.color }}
                variants={itemVariants}
                whileHover={
                  prefersReducedMotion
                    ? undefined
                    : { y: -8, scale: 1.03, boxShadow: '0 15px 30px rgba(0,0,0,0.4)' }
                }
              >
                <div style={iconWrapStyle} aria-hidden="true">{s.icon}</div>
                <h3 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: 15 }}>
                  {s.title}
                </h3>
                <p style={{ fontSize: '1rem', color: '#e5e7eb', lineHeight: 1.6 }}>{s.description}</p>
              </motion.article>
            ))}
          </motion.div>
        </section>

        <section aria-labelledby="why-heading">
          <motion.div style={sectionShell} variants={itemVariants}>
            <h2
              id="why-heading"
              style={{
                ...gradientText,
                fontSize: 'clamp(1.75rem, 1.5vw + 1rem, 2.5rem)',
                fontWeight: 700,
                marginBottom: 24,
                textAlign: 'center',
              }}
            >
              Why Our Design Delivers
            </h2>

            <p
              style={{
                fontSize: 'clamp(1rem, 0.4vw + 0.85rem, 1.2rem)',
                color: '#d1d5db',
                maxWidth: 800,
                margin: '0 auto 40px',
                lineHeight: 1.7,
                textAlign: 'center',
              }}
            >
              We pair human creativity with AI tooling, maintain brand systems for consistency, and iterate based on feedback and data—so visuals are beautiful, on‑brand, and effective.
            </p>

            <ul style={listGrid}>
              {[
                'Human‑in‑the‑loop AI to keep quality high and artifacts low',
                'Reusable design systems to accelerate production without drift',
                'Accessibility‑conscious color and type choices',
                'Versioned assets and handoff‑ready files for teams',
                'Rapid exploration with structured review cycles',
              ].map((item, index) => (
                <motion.li
                  key={item}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    padding: 16,
                    background: 'rgba(30,41,59,0.5)',
                    borderRadius: 12,
                    borderLeft: '4px solid #ec4899',
                  }}
                  initial={{ opacity: 0, x: prefersReducedMotion ? 0 : -20 }}
                  animate={{
                    opacity: 1,
                    x: 0,
                    transition: prefersReducedMotion
                      ? { duration: 0 }
                      : { delay: 0.4 + index * 0.08 },
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      display: 'inline-block',
                      marginRight: 12,
                      color: '#ec4899',
                      fontSize: '1.5rem',
                    }}
                  >
                    •
                  </span>
                  <span style={{ color: '#e5e7eb', lineHeight: 1.6 }}>{item}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </section>

        <section aria-labelledby="contact-heading" style={{ marginTop: 64 }}>
          <h2 id="contact-heading" style={{ position: 'absolute', left: -10000 }}>
            Contact Us
          </h2>
          <ContactForm />
        </section>
      </motion.div>
    </main>
  );
};

export default GraphicsDesign;
